/* ============================================================================
   olmia-keuzehulp.js — "Wat wil je automatiseren?" flow (ported from design
   assets/js/keuzehulp.js). 4 stappen: toepassing → gewicht → tempo → ruimte →
   concreet advies. De beslistabel (cobotTable, gripper/vision/onderstel/service
   -regels, rationale-teksten, notes) is 1-op-1 overgenomen. Elke PRODUCTS_BY_ID
   -lookup is vervangen door de keuzehulp-map.json data (één keer opgehaald; alle
   handles in de tabel bestaan in de map). Custom regels (Vaste robotvoet / Mobiel
   onderstel / Installatie & training) = map-entries fixed-base/mobile-base/install:
   design sub-copy, afbeelding olmia-mark.png, UNGELINKT gerenderd.
   Product-regels linken naar map.url. Bedragen lopen door OlmiaUI.Money — de
   enige geldformatter van het thema (nl-NL EUR, centen alleen als ze er zijn).
   Regelbedragen én het totaal worden in HELE CENTEN opgeteld en pas op het eind
   geformatteerd; nooit een som van reeds afgeronde bedragen.

   WAAR DE BEDRAGEN VANDAAN KOMEN (gewijzigd, ROADBLOCKS #27) — NIET meer uit
   keuzehulp-map.json. Dat is een thema-asset: die staat op de CDN, is met een kale
   URL op te vragen en kán dus geen prijsmuur hebben. De map draagt nu alleen nog
   structuur (handle/titel/foto/payload/reach/categorie/shortDesc). De bedragen komen
   uit #khPrices, een JSON-blok dat sections/keuzehulp.liquid server-side rendert via
   snippets/product-card.liquid — dezelfde poort als de productkaarten en de PDP.
   Daar valt de tier-beslissing (QUOTE > STRICT > SOFT), één keer, op de server.

   Deze module beslist dus NIET meer of iets afgeschermd is; hij leest af of er een
   bedrag geleverd is (price === null → afgeschermd) en kiest alleen nog het LABEL:
   price_on_request → "Prijs op aanvraag" → /pages/contact voor IEDEREEN (ook b2b);
   anders gast → "Log in voor prijzen" → /account/login, ingelogd → "Prijs op aanvraag".
   De guestWall-controle hieronder is een FAIL-CLOSED backstop: hij kan alleen extra
   afschermen, nooit iets onthullen. CTA volgt het DEEPLINK-contract.

   Editor-safe (see olmia-editor.js): the wizard is (re)bound inside init(root), scoped
   to the #kh section root; the auth signals are re-read from the fresh root and the
   step listeners are guarded (data-kh-bound) so a re-render rebinds the new nodes
   without stacking handlers. No timers/observers/window listeners → teardown no-op.
   ========================================================================== */
/* TOESTEMMING (gemeten 2026-08-19, scripts/verify-toestemming.mjs): de keuzehulp
   schrijft NIETS naar cookie, localStorage of sessionStorage. De antwoorden staan in
   het object `answers` en gaan via URLSearchParams de link in (?app=…), zodat een
   bezoeker zijn uitkomst kan delen. Een URL is geen opslag op het apparaat: artikel
   11.7a Telecommunicatiewet raakt hem niet. Houd dat zo. */
(function () {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  const nlNum = (n) => new Intl.NumberFormat('nl-NL').format(n);
  /* PRICE RULE: er is één geldformatter, in olmia-ui.js (OlmiaUI.Money). Hier
     stond een eigen Intl.NumberFormat met 0 decimalen; die maakte van € 220,60
     een € 221 en kon los van de rest verlopen.
     LET OP de volgorde: layout/theme.liquid laadt olmia-ui.js NA
     {{ content_for_layout }}, dus dit sectie-script draait er vóór. Daarom wacht
     de init onderaan dit bestand op DOMContentLoaded — pas dán bestaat OlmiaUI. */
  const money = (eur) => OlmiaUI.Money.format(eur);
  const cents = (eur) => OlmiaUI.Money.cents(eur);
  const fromCents = (c) => OlmiaUI.Money.fromCents(c);
  const LOGIN_URL = '/account/login';
  // Vertaalbare teksten (window.olmiaT uit de layout); de tweede parameter is de Nederlandse terugval.
  const T = (k, f) => (window.olmiaT ? window.olmiaT(k, f) : f);
  const fmt = (s, o) => s.replace(/\{(\w+)\}/g, (m, k) => (k in o ? o[k] : m));

  // ---- module state (auth signals re-read per init because #kh is replaced) ----
  let root = null;
  let loggedIn = false, isB2b = false, guest = true, guestWall = false;
  let GATE_HREF = LOGIN_URL, GATE_TEXT = T('common.login_for_prices', 'Log in voor prijzen'), TOTAL_TEXT = T('common.login_for_total', 'Log in voor totaalprijs');
  let MAP_URL = null, MARK_URL = null;
  // Mag de tweede adviesknop (#khAlt) de chat kapen? Merchant-instelling
  // (sections/keuzehulp.liquid → alt_opens_chat). Standaard NEE: dan is het een
  // gewone link naar een pagina die bestaat. Zie de klik-afhandelaar in bind().
  let ALT_CHAT = false;
  // Server-geleverde meerprijs (dubbel palletstation). 0 als de bezoeker de
  // Palletbooster-prijs niet mag zien — dit asset bevat zelf geen bedrag.
  let DUBBEL_DELTA = 0;

  let MAP = null;
  let mapPromise = null;
  function loadMap() {
    // Faalt de map (CDN weg, kapotte JSON), dan blijft MAP leeg. Dat is geen ramp
    // meer: sinds 2026-08-20 levert de server elke spec zelf mee (zie M() hieronder).
    if (!mapPromise) mapPromise = fetch(MAP_URL).then((r) => r.json()).then((d) => { MAP = d; return d; }).catch(() => { MAP = {}; return MAP; });
    return mapPromise;
  }

  /* ---- WAAR DE SPECS VANDAAN KOMEN (gewijzigd 2026-08-20, ROADBLOCKS #73) ----
     Niet meer uit keuzehulp-map.json. Dat asset wordt door `seed.mjs` gegenereerd
     en werd tot vandaag gevuld uit de ONTWERPMOCKUP (design assets/js/catalog.js,
     juli), niet uit de winkel. Gemeten 2026-08-20: negen afwijkingen tegenover
     /products/<handle> van dezelfde winkel, de grootste 600 mm — de keuzehulp
     beloofde "Dobot CR20A … 1.100 mm bereik" waar de productpagina 1.700 mm zegt.

     #khPrices (sections/keuzehulp.liquid) draagt per handle het `data-product`-JSON
     van snippets/product-card.liquid, aangevuld met payloadMax + shortDesc uit
     dezelfde all_products-lookup. Dat is per request winkelwaarheid en kan dus
     niet verouderen.

     TERUGVAL IS PER HANDLE, NOOIT PER VELD. Leverde de server een handle, dan is
     de server autoritair voor ÉLK veld ervan — ook voor "leeg". Een terugval per
     veld zou precies de verouderde mockupgetallen terughalen die hier weg moeten:
     wie `reach_mm` in de winkel wist, hoort geen 1.100 mm uit een asset te krijgen. */
  const M = (id) => {
    const s = PRICES[id];
    const m = (MAP && MAP[id]) || null;
    if (!s) return m;                       // niets van de server → de map (of null)
    return {
      handle: s.handle || id,
      title: s.title || '',
      brand: s.brand || '',
      url: s.url || ('/products/' + id),
      image: s.image || '',
      payloadKg: s.payload == null ? null : s.payload,
      payloadMaxKg: s.payloadMax == null ? null : s.payloadMax,
      reachMm: s.reach == null ? null : s.reach,
      shortDesc: s.shortDesc == null ? null : s.shortDesc,
      category: s.category == null ? null : s.category,
      variantId: s.variantId == null ? null : s.variantId,
    };
  };

  // Server-side gepoorte prijsdata (#khPrices). Per handle: { price, cust,
  // priceOnRequest, … } — precies het data-product-JSON van de productkaart.
  // price === null betekent: deze bezoeker mag geen bedrag zien. Ontbreekt een
  // handle helemaal, dan is er niets geleverd en schermen we af (fail-closed).
  let PRICES = {};
  const P = (id) => PRICES[id] || {};

  /* ---- BESTAAT DIT PRODUCT VOOR DEZE BEZOEKER? (2026-08-31) -----------------
     De beslistabel hieronder noemt handles. Na de winkelverhuizing naar
     ciscx6-33 waren zeven van de elf dood: `ur15`, `ur8-long`, `onrobot-2fg14`,
     `onrobot-eyes` en `pickit-mhd2` bestonden niet meer, `palletbooster` en
     `fanuc-crx-3ia` stonden op concept. De adviespagina bleef ze noemen, met een
     link naar HTTP 404, en `verify-decimalen` mat nog maar "1 productregel van 1"
     waar er minstens twee horen te staan.

     De autoriteit is de SERVER, niet dit bestand en niet keuzehulp-map.json.
     `#khPrices` wordt in sections/keuzehulp.liquid gevuld met `all_products[handle]`,
     en Liquid geeft daar niets terug voor een product dat niet bestaat OF op concept
     staat. Staat een handle in PRICES, dan kan de bezoeker de pagina openen. Punt.

     De map blijft alleen de terugval voor het geval de server niets leverde
     (sectie afwezig, JSON kapot); dan is een advies uit de map beter dan geen
     advies. Let op: de map wordt door seed.mjs gegenereerd en bevatte tot vandaag
     ook conceptproducten — daarom is hij NOOIT de eerste bron. */
  const serverLeverde = () => Object.keys(PRICES).length > 0;
  const leeft = (id) => {
    if (!id) return true;                       // een regel zonder handle bestaat altijd
    if (serverLeverde()) return Object.prototype.hasOwnProperty.call(PRICES, id);
    return !!(MAP && MAP[id]);
  };
  // Mag deze bezoeker het bedrag van deze regel zien? Enige bron: de server.
  // De guestWall-clausule is een backstop die alleen extra afschermt.
  const priceOf = (id) => {
    const pr = P(id);
    if (pr.price == null) return null;
    if (!isB2b && guest && guestWall) return null;
    return pr.price;
  };
  const custOf = (id) => { const pr = P(id); return pr.cust == null ? null : pr.cust; };
  const onRequest = (id) => !!P(id).priceOnRequest;

  const APPS = [
    { key: 'palletiseren',   label: T('tools.kh_app_pallet_label', 'Palletiseren'),       sub: T('tools.kh_app_pallet_sub', 'Dozen op pallet of rolcontainer'), icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="13" width="8" height="6" rx="1"/><rect x="13" y="13" width="8" height="6" rx="1"/><rect x="8" y="5" width="8" height="6" rx="1"/></svg>' },
    { key: 'machinebelading', label: T('tools.kh_app_machine_label', 'Machinebelading'),   sub: T('tools.kh_app_machine_sub', 'CNC, pers of spuitgietmachine'),   icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="8" width="13" height="11" rx="2"/><path d="M16 12h5M19 9l3 3-3 3M7 8V5h5v3"/></svg>' },
    { key: 'pickplace',      label: T('tools.kh_app_pickplace_label', 'Pick & place'),       sub: T('tools.kh_app_pickplace_sub', 'Oppakken, sorteren, inpakken'),    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 17h6M4 17V7h6v10M14 7l6 5-6 5"/></svg>' },
    { key: 'lassen',         label: T('tools.kh_app_welding_label', 'Lassen'),             sub: T('tools.kh_app_welding_sub', 'MIG/MAG- of TIG-laswerk'),         icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M13 3 5 13h6l-2 8 8-10h-6z"/></svg>' },
    { key: 'schuren',        label: T('tools.kh_app_sanding_label', 'Schuren & polijsten'), sub: T('tools.kh_app_sanding_sub', 'Oppervlaktebewerking'),           icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="1.6"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>' },
    { key: 'assemblage',     label: T('tools.kh_app_assembly_label', 'Assemblage'),         sub: T('tools.kh_app_assembly_sub', 'Monteren, schroeven, inspectie'),  icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 7a4 4 0 1 0-4 4l7 7 3-3-7-7a4 4 0 0 0 1-1z"/></svg>' },
  ];
  const APP_BY_KEY = Object.fromEntries(APPS.map(a => [a.key, a]));

  const answers = { app: null, weight: null, tempo: null, space: null };
  let current = 1;

  /* ---------------- decision table ---------------- */
  // returns { lines:[{ref?|custom}], rationale, ctaLabel, ctaHref, altLabel, altAction, note? }
  function advise() {
    const { app, weight, tempo, space } = answers;
    const SVC_INSTALL = { custom: T('tools.kh_line_install', 'Installatie & training on-site'), sub: T('tools.kh_line_install_sub', 'Door Olmia, draaiend opgeleverd'), handle: 'install' };
    let lines = []; let rationale = ''; let ctaLabel = T('tools.kh_cta_bundle', 'Open in de bundel-bouwer'); let ctaHref = ''; let note = '';

    if (app === 'palletiseren') {
      const dubbel = tempo === 'high' && space !== 'tight';
      const pb = M('palletbooster');
      // De meerprijs voor het dubbele palletstation komt server-side binnen
      // (#kh data-dubbel-delta) en is 0 zodra deze bezoeker de Palletbooster-prijs
      // niet mag zien — geen bedrag in dit asset. Zie sections/keuzehulp.liquid.
      lines.push({ ref: 'palletbooster', sub: dubbel ? T('tools.kh_line_pallet_double', 'Dubbel palletstation: doorpalletiseren zonder stop') : T('tools.kh_line_pallet_single', 'Enkel palletstation, compact en compleet'), delta: dubbel ? DUBBEL_DELTA : 0 });
      rationale = fmt(T('tools.kh_rationale_pallet', 'voor palletiseren is onze eigen Palletbooster vrijwel altijd de beste route: complete cel met software, geen programmeerkennis nodig{heavy}. Bewezen bij o.a. Haarspullen.nl.'), { heavy: weight === 'heavy' ? T('tools.kh_rationale_pallet_heavy', ', en we stemmen de cobot af op je zwaardere dozen') : '' });
      note = T('tools.kh_note_pallet', 'Liever losse componenten? Dat kan ook: open de bundel-bouwer en stel zelf een palletiseeropstelling samen.');
      // De knop wijst alleen naar de productpagina als die pagina er ook is. Op
      // ciscx6-33 staat `palletbooster` op concept en gaf /products/palletbooster
      // HTTP 404 — het enige adviespad dat de bezoeker rechtstreeks een dode link
      // in handen gaf. Zolang dat zo is opent de knop de bouwer, precies zoals bij
      // de vijf andere paden; publiceert Olmia het product, dan komt de PDP-knop
      // vanzelf terug. Geen bestaande tekst is hiervoor verzonnen of veranderd.
      if (leeft('palletbooster')) {
        ctaLabel = T('tools.kh_cta_palletbooster', 'Bekijk de Palletbooster');
        ctaHref = '/products/palletbooster';
      } else {
        ctaLabel = T('tools.kh_cta_bundle', 'Open in de bundel-bouwer');
        ctaHref = '/pages/opstelling';
      }
      return { lines, rationale, ctaLabel, ctaHref, note };
    }

    // pick cobot per application × weight
    /* HANDLES HERSTELD NA DE WINKELVERHUIZING (2026-08-31, ciscx6-33)
       Dit zijn dezelfde producten, met de handle die de nieuwe winkel ze gaf:
         ur15          -> universal-robots-ur15        (Universal Robots UR15, actief)
         ur8-long      -> universal-robots-ur8-long    (Universal Robots UR8 Long, actief)
         onrobot-2fg14 -> 2fg14                        (OnRobot 2FG14, actief)
         onrobot-eyes  -> eyes                         (OnRobot Eyes, actief)
       Elk van de vier is in de Admin API opgezocht, niet geraden. */
    const UR15 = 'universal-robots-ur15';
    const UR8L = 'universal-robots-ur8-long';
    const cobotTable = {
      machinebelading: { light: 'dobot-cr10a', mid: UR15, heavy: 'dobot-cr30h' },
      pickplace:       { light: 'dobot-cr10a', mid: UR15, heavy: 'dobot-cr20a' },
      lassen:          { light: space === 'roomy' ? UR8L : UR15, mid: space === 'roomy' ? UR8L : UR15, heavy: UR15 },
      schuren:         { light: space === 'tight' ? UR15 : UR8L, mid: space === 'tight' ? UR15 : UR8L, heavy: UR15 },
      assemblage:      { light: 'fanuc-crx-3ia', mid: UR15, heavy: 'dobot-cr20a' },
    };
    const cobotId = cobotTable[app][weight];
    lines.push({ ref: cobotId, sub: cobotSub(cobotId) });

    // gripper
    if (app === 'machinebelading' || app === 'assemblage') lines.push({ ref: '2fg14', sub: T('tools.kh_line_gripper_parallel', 'Parallelgripper, stevige grip op werkstukken') });
    if (app === 'pickplace') lines.push({ ref: 'vgp30', sub: T('tools.kh_line_gripper_vacuum', 'Elektrische vacuümgripper, geen perslucht nodig') });

    // vision bij hoog tempo / ongeordende aanvoer
    /* VISION — de beslistabel koos voor machinebelading de Pickit M-HD2 (3D
       bin-picking) en voor pick & place de OnRobot Eyes. `pickit-mhd2` bestaat op
       deze winkel NIET, ook niet als concept: Olmia voert Pickit hier niet.
       De regel wees dus naar een 404. Er wordt hier geen nieuw product bedacht;
       beide takken adviseren nu het vision-systeem dat de winkel wél gepubliceerd
       heeft. Komt Pickit alsnog in de catalogus, dan hoort deze splitsing terug —
       dat staat als open punt in specs/nachtrun-2026-08-31.md. */
    if (tempo === 'high' && (app === 'machinebelading' || app === 'pickplace')) {
      lines.push({ ref: 'eyes', sub: T('tools.kh_line_vision', 'Vision voor ongeordende aanvoer op tempo') });
    }

    // onderstel
    if (app !== 'lassen' && app !== 'schuren') {
      if (space === 'roomy') lines.push({ custom: T('tools.kh_line_mobile_base', 'Mobiel onderstel op wielen'), sub: T('tools.kh_line_mobile_base_sub', 'Cel flexibel verplaatsbaar tussen werkplekken'), handle: 'mobile-base', q: ['base', 'mobile-base'] });
      else lines.push({ custom: T('tools.kh_line_fixed_base', 'Vaste robotvoet'), sub: T('tools.kh_line_fixed_base_sub', 'Stabiel en compact'), handle: 'fixed-base', q: ['base', 'fixed-base'] });
    }

    lines.push({ ...SVC_INSTALL, q: ['service', 'install'] });

    const why = {
      machinebelading: T('tools.kh_why_machine', 'machinebelading vraagt om betrouwbaar herladen, dag in dag uit'),
      pickplace: T('tools.kh_why_pickplace', 'pick & place draait om tempo en een gripper die alles pakt'),
      lassen: T('tools.kh_why_welding', 'voor laswerk telt een stabiele boog en een rustige, herhaalbare beweging'),
      schuren: T('tools.kh_why_sanding', 'bij schuren en polijsten telt bereik en een constante aandrukkracht'),
      assemblage: T('tools.kh_why_assembly', 'assemblage vraagt om precisie en herhaalbaarheid'),
    };
    // Specs die de winkel niet levert worden WEGGELATEN, niet als 0 geschreven:
    // `nlNum(null)` gaf "0" en dus "0 mm bereik" — een getal dat niemand ooit
    // heeft vastgesteld. Zelfde principe als een afgeschermde prijs (#18/#31).
    const cp = M(cobotId);
    const specBits = cp ? [fmtPayload(cp), fmtReach(cp)].filter(Boolean) : [];
    rationale = cp && cp.title
      ? (specBits.length
        ? fmt(T('tools.kh_rationale_specs', '{why}. {title} past daar met {specs} het best bij.'), { why: why[app], title: cp.title, specs: specBits.join(T('tools.kh_and', ' en ')) })
        : fmt(T('tools.kh_rationale_nospecs', '{why}. {title} past daar het best bij.'), { why: why[app], title: cp.title }))
      : fmt(T('tools.kh_rationale_nocobot', '{why}.'), { why: why[app] });
    if (app === 'lassen') note = T('tools.kh_note_welding', 'De lastoorts en het laspakket stemmen we af op jouw proces; dat offreren we na de gratis productiescan.');
    if (app === 'schuren') note = T('tools.kh_note_sanding', 'Kracht-gestuurd schuren stemmen we af op je product; de juiste kop en krachtregeling offreren we na de productiescan.');

    // deeplink naar de opstelling-bouwer, voorgevuld met cobot + opties
    /* Eerst opschonen, dan pas de deeplink bouwen. Deed je dit andersom, dan
       droeg de knop nog `base=fixed-base&service=install` mee — parameters voor
       stappen die de bouwer niet kent en dus stil weggooit. Gemeten op ciscx6-33
       vóór deze regel: vijf van de zes adviespaden stuurden zulke lege parameters
       mee. `leeft()` is de toets; zie de uitleg bij die functie. */
    const kwijt = lines.filter(l => !leeft(l.ref || l.handle)).map(l => l.ref || l.handle);
    if (kwijt.length) lines = lines.filter(l => leeft(l.ref || l.handle));

    const q = new URLSearchParams();
    // Ook de cobot zelf moet bestaan. `fanuc-crx-3ia` staat op ciscx6-33 op concept;
    // de adviesregel viel daardoor terecht weg, maar de knop droeg nog
    // `cobot=fanuc-crx-3ia` mee naar een kaart die de bouwer niet heeft.
    if (leeft(cobotId)) q.set('cobot', cobotId);
    const grip = lines.find(l => l.ref && M(l.ref)?.category === 'grippers');
    const vis = lines.find(l => l.ref && M(l.ref)?.category === 'vision');
    if (grip) q.set('gripper', grip.ref);
    if (vis) q.set('vision', vis.ref);
    lines.forEach(l => { if (l.q) q.set(l.q[0], l.q[1]); });
    ctaLabel = T('tools.kh_cta_builder', 'Open in de opstelling-bouwer');
    ctaHref = '/pages/opstelling?' + q.toString();
    return { lines, rationale, ctaLabel, ctaHref, note, kwijt };
  }
  function cobotSub(id) { const p = M(id); if (!p) return ''; return [fmtPayload(p), fmtReach(p)].filter(Boolean).join(' · '); }
  function fmtPayload(p) {
    if (!p || p.payloadKg == null) return '';
    return p.payloadMaxKg != null ? fmt(T('tools.kh_payload_max', '{kg} kg payload (tot {max} kg)'), { kg: nlNum(p.payloadKg), max: nlNum(p.payloadMaxKg) }) : fmt(T('tools.kh_payload', '{kg} kg payload'), { kg: nlNum(p.payloadKg) });
  }
  function fmtReach(p) { return (!p || p.reachMm == null) ? '' : fmt(T('tools.kh_reach', '{mm} mm bereik'), { mm: nlNum(p.reachMm) }); }

  /* ---------------- render ---------------- */
  function renderApps() {
    $('#khApps').innerHTML = APPS.map(a => `
      <button class="kh-tile${answers.app === a.key ? ' active' : ''}" data-app="${a.key}">
        <span class="ic">${a.icon}</span><b>${a.label}</b><span>${a.sub}</span>
      </button>`).join('');
  }

  function goTo(step) {
    current = step;
    $$('.kh__step').forEach(s => s.classList.toggle('active', s.dataset.step === String(step)));
    const idx = step === 'result' ? 4 : step;
    $('#khStepLabel').textContent = step === 'result' ? T('tools.kh_step_result', 'Advies') : fmt(T('tools.kh_step_label', 'Stap {n} van 4'), { n: step });
    // half-stap-model: op stap 1 net begonnen (12,5%) → oploopend naar 100% bij het advies
    $('#khBar').style.width = (step === 'result' ? 100 : (idx - 0.5) * 25) + '%';
    $('#khRestart').classList.toggle('hide', step === 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function showResult() {
    await loadMap();
    const a = advise();
    const app = APP_BY_KEY[answers.app];
    $('#khResultTitle').textContent = fmt(T('tools.kh_result_title', 'Ons advies voor {app}'), { app: app.label.toLowerCase() });
    $('#khResultSub').textContent = T('tools.kh_result_sub', 'Op basis van je antwoorden. Pas gerust een antwoord aan, het advies rekent direct mee.');
    $('#khRationale').textContent = a.rationale;

    // anyLoginGate = a row hidden by the STRICT/SOFT wall — a login (or the b2b tag)
    //                can still reveal it. anyQuote = a price_on_request row, which no
    //                login ever reveals.
    /* OPTELLEN IN HELE CENTEN (PRICE RULE). `pub` en `cust` zijn centen, geen
       euro's: een som van afgeronde euro-bedragen wijkt af van het afgeronde
       totaal, en dat verschil belandde eerder zichtbaar in #khTotal. */
    let pub = 0, cust = 0, visibleCount = 0, anyLoginGate = false, anyQuote = false;
    const gateRow = `<span class="price__login">${GATE_TEXT}</span>`;
    // A quote-only (price_on_request) row is never "log in" — it is an offerte for
    // everyone, b2b included, so it carries its own label instead of GATE_TEXT.
    const quoteRow = `<span class="price__login">${T('common.price_on_request', 'Prijs op aanvraag')}</span>`;
    /* GEEN ADVIES NAAR EEN PAGINA DIE ER NIET IS. `leeft()` toetst tegen de
       server-payload; zie de uitleg daar. Dit vangt ook de opstelling-opties
       `fixed-base`, `mobile-base` en `install`, die op deze winkel geen product
       zijn: die regels stonden er met "Log in voor prijzen" naast een artikel dat
       niet bestaat, en hun deeplink (`base=`, `service=`) opende een stap die de
       bouwer niet meer toont. */
    const doodInAdvies = (a.kwijt || []).concat(
      a.lines.filter(l => !leeft(l.ref || l.handle)).map(l => l.ref || l.handle));
    a.lines = a.lines.filter(l => leeft(l.ref || l.handle));
    // Meetbaar houden in plaats van stil wegpoetsen: een suite (en een mens in de
    // inspector) kan zo zien WELK product ontbrak, niet alleen dat er een regel mist.
    if (doodInAdvies.length) root.setAttribute('data-kh-overgeslagen', doodInAdvies.join(' '));
    else root.removeAttribute('data-kh-overgeslagen');
    $('#khLines').innerHTML = a.lines.map(l => {
      if (l.ref) {
        const p = M(l.ref);
        // Geen server-data én geen map-entry = een product dat deze winkel niet
        // (meer) kent. Dan verdwijnt de regel; een kaart met een lege titel en
        // een dode link is erger dan geen kaart.
        if (!p) return '';
        const extra = l.delta || 0;
        // De server heeft de tier-beslissing al genomen: kreeg deze bezoeker een
        // bedrag, dan mag hij het zien. Zo niet, dan kiezen we hier alléén nog het
        // label. Een quote-only regel (price_on_request) is voor IEDEREEN — ook b2b —
        // "Prijs op aanvraag"; geen enkele login onthult die. De rij blijft een link
        // naar de PDP (een geneste <a> zou ongeldig zijn), dus de gate staat alleen
        // in het prijsvak.
        const amount = priceOf(l.ref);
        if (amount == null) {
          if (onRequest(l.ref)) {
            anyQuote = true;
            return row(p.image, p.title, l.sub || p.shortDesc, quoteRow, p.url);
          }
          anyLoginGate = true;
          return row(p.image, p.title, l.sub || p.shortDesc, gateRow, p.url);
        }
        const lineCents = cents(amount) + cents(extra);
        pub += lineCents; cust += cents(custOf(l.ref) ?? amount) + cents(extra); visibleCount++;
        return row(p.image, p.title, l.sub || p.shortDesc, fromCents(lineCents), p.url);
      }
      // Custom rows (robotvoet / onderstel / installatie) zijn echte producten met een
      // eigen handle; hun bedrag loopt door dezelfde server-poort.
      const amount = priceOf(l.handle);
      if (amount == null) {
        if (onRequest(l.handle)) { anyQuote = true; return row(MARK_URL, l.custom, l.sub, quoteRow); }
        anyLoginGate = true;
        return row(MARK_URL, l.custom, l.sub, gateRow);
      }
      pub += cents(amount); cust += cents(custOf(l.handle) ?? amount); visibleCount++;
      return row(MARK_URL, l.custom, l.sub, money(amount));
    }).join('');

    // Total, in order of honesty:
    //   1. GUEST with a login-fixable row  → "Log in voor totaalprijs" → /account/login.
    //      (soft wall hides every row; without the wall a strict row triggers this.)
    //   2. nothing summable at all         → a gate link: login copy when a login would
    //      still help, otherwise the honest "Totaal op aanvraag" → /pages/contact (this
    //      is the only destination for a quote-only advice, b2b included).
    //   3. partial total                   → sum of the visible rows + the short suffix
    //      "excl. prijs op aanvraag". A price_on_request row lands here for EVERY
    //      visitor: no login (and no b2b tag) can ever reveal it.
    //   4. b2b, nothing gated              → customerPrice total, unchanged.
    //   5. everything visible              → plain public total.
    /* `.amt--gate` — DE GATE-TEKST IS GEEN BEDRAG (gemeten 2026-08-21).
       `#khTotal` draagt `.amt`: 1,9rem Bree Serif indigo (templates.css r576) —
       de opmaak van een BEDRAG. Stond er tekst in plaats van een bedrag, dan
       erfde die tekst de bedragopmaak — 30,4 px schreefletter, onderstreept,
       over de volle vakbreedte. Dat las als een kapotte terugval.
       De klas wordt hier gezet omdat ALLEEN dit punt weet of er een bedrag
       staat; de opmaak zelf staat scoped in sections/keuzehulp.liquid. */
    const totalEl = $('#khTotal');
    const setTotal = (html, gated) => {
      totalEl.classList.toggle('amt--gate', !!gated);
      totalEl.innerHTML = html;
    };
    /* NIETS TE TELLEN IS GEEN NUL (2026-08-31). Valt elke adviesregel weg omdat het
       product niet bestaat of op concept staat, dan liep de laatste tak hieronder op
       `fromCents(0)` en stond er "Totaalprijs € 0" op het scherm. Gemeten op het pad
       palletiseren, waar `palletbooster` op concept staat. Een bedrag van nul euro is
       een bewering over geld die niemand heeft gedaan; dan hoort er niets te staan.
       Label én vak gaan samen weg, zodat er geen kop zonder waarde overblijft. */
    const nietsTeTellen = a.lines.length === 0;
    for (const el of $$('[data-kh-total-row]')) el.hidden = nietsTeTellen;
    if (nietsTeTellen) {
      setTotal('<small id="khTotalCust"></small>', false);
      const cta0 = $('#khCta'); cta0.textContent = a.ctaLabel; cta0.href = a.ctaHref;
      $('#khNote').textContent = (a.note ? a.note + ' ' : '') + T('tools.kh_note_indication', 'Indicatie op basis van je antwoorden. Voor een definitief voorstel doet Olmia kosteloos een productiescan op locatie.');
      goTo('result');
      return;
    }
    if (guest && anyLoginGate) {
      setTotal(`<a class="price__login" href="${LOGIN_URL}">${TOTAL_TEXT}</a>`, true);
    } else if (visibleCount === 0 && (anyLoginGate || anyQuote)) {
      const totHref = anyLoginGate ? GATE_HREF : '/pages/contact';
      const totText = anyLoginGate ? TOTAL_TEXT : T('tools.kh_total_on_request', 'Totaal op aanvraag');
      setTotal(`<a class="price__login" href="${totHref}">${totText}</a>`, true);
    } else if (anyLoginGate || anyQuote) {
      const showCust = isB2b && cust < pub;
      setTotal(`${fromCents(showCust ? cust : pub)}<small id="khTotalCust">${T('tools.kh_total_excl_quote', 'excl. prijs op aanvraag')}</small>`, false);
    } else if (isB2b) {
      const showCust = cust < pub;
      setTotal(`${fromCents(showCust ? cust : pub)}<small id="khTotalCust">${showCust ? fmt(T('tools.kh_customer_price', 'jouw klantprijs · publiek {price}'), { price: fromCents(pub) }) : ''}</small>`, false);
    } else {
      setTotal(`${fromCents(pub)}<small id="khTotalCust"></small>`, false);
    }
    const cta = $('#khCta'); cta.textContent = a.ctaLabel; cta.href = a.ctaHref;
    $('#khNote').textContent = (a.note ? a.note + ' ' : '') + T('tools.kh_note_indication', 'Indicatie op basis van je antwoorden. Voor een definitief voorstel doet Olmia kosteloos een productiescan op locatie.');
    goTo('result');
  }
  const row = (img, name, sub, price, url) => {
    const tag = url ? 'a' : 'div';
    const attr = url ? ` href="${url}"` : '';
    return `
    <${tag} class="kh-line${url ? ' kh-line--link' : ''}"${attr}>
      <span class="kh-line__img"><img src="${img}" alt=""></span>
      <span class="kh-line__t"><b>${name}</b><span>${sub || ''}</span></span>
      <span class="kh-line__p">${price}</span>
      ${url
        ? '<span class="kh-line__chev" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"/></svg></span>'
        : '<span class="kh-line__chev kh-line__chev--ghost" aria-hidden="true"></span>'}
    </${tag}>`;
  };

  /* ---------------- events ---------------- */
  function bind() {
    // Bind once per #kh root — a re-render gives a fresh root (rebinds new nodes).
    if (root.dataset.khBound === '1') return;
    root.dataset.khBound = '1';
    $('#khApps').addEventListener('click', (e) => {
      const t = e.target.closest('[data-app]'); if (!t) return;
      answers.app = t.dataset.app; renderApps(); setTimeout(() => goTo(2), 180);
    });
    $$('.kh-opts').forEach(box => box.addEventListener('click', (e) => {
      const o = e.target.closest('.kh-opt'); if (!o) return;
      answers[box.dataset.q] = o.dataset.v;
      $$('.kh-opt', box).forEach(x => x.classList.remove('active')); o.classList.add('active');
      const next = { weight: 3, tempo: 4 }[box.dataset.q];
      setTimeout(() => next ? goTo(next) : showResult(), 200);
    }));
    $$('[data-back]').forEach(b => b.addEventListener('click', () => goTo(current === 'result' ? 4 : Math.max(1, current - 1))));
    $('#khRestart').addEventListener('click', () => { answers.app = answers.weight = answers.tempo = answers.space = null; renderApps(); $$('.kh-opt').forEach(x => x.classList.remove('active')); goTo(1); });
    /* #khAlt — "Plan een gratis productiescan".
       DIT WAS EEN DODE KNOP (gemeten 2026-08-21, 8 adviespaden, gast). De href
       stond op "#" en deze afhandelaar deed onvoorwaardelijk preventDefault()
       om de verborgen chatbrug aan te klikken. Bestond die brug niet, of laadde
       Smartsupp niet (geen toestemming, adblocker, vendor plat), dan gebeurde er
       NIETS — en midden-klik / ctrl-klik / "open in nieuw tabblad" / geen-JS
       leverden sowieso niets op. De href draagt nu een echte bestemming
       (sections/keuzehulp.liquid, standaard /pages/contact).
       De chat is nog steeds bereikbaar, maar alleen als de merchant daarvoor
       kiest EN de brug er echt staat. In elk ander geval doen we niets en volgt
       de browser de link — de knop kan dus niet meer dood zijn. */
    $('#khAlt').addEventListener('click', (e) => {
      if (!ALT_CHAT) return;
      const bridge = document.getElementById('chatLauncher');
      if (!bridge) return;
      e.preventDefault();
      bridge.click();
    });
  }

  function init(sectionRoot) {
    const scope = sectionRoot || document;
    const r = scope.querySelector('#kh');
    if (!r) return;
    root = r;
    // Auth-signalen (server-side gezet) — TWEE TIERS (re-read per init).
    loggedIn = root.dataset.loggedIn === '1';
    isB2b = document.body.dataset.b2b === '1';
    guest = !loggedIn;
    guestWall = root.dataset.guestWall === '1';
    GATE_HREF = loggedIn ? '/pages/contact' : LOGIN_URL;
    GATE_TEXT = loggedIn ? T('common.price_on_request', 'Prijs op aanvraag') : T('common.login_for_prices', 'Log in voor prijzen');
    TOTAL_TEXT = loggedIn ? T('tools.kh_total_on_request', 'Totaal op aanvraag') : T('common.login_for_total', 'Log in voor totaalprijs');
    MAP_URL = root.dataset.mapUrl;
    MARK_URL = root.dataset.markUrl;
    DUBBEL_DELTA = Number(root.dataset.dubbelDelta || 0) || 0;
    ALT_CHAT = root.dataset.altChat === '1';
    // Prijsdata per init opnieuw inlezen: bij een sectie-herrender is #kh (en dus
    // #khPrices) vervangen. Geen blok of kapotte JSON → PRICES leeg → alles
    // afgeschermd (fail-closed), nooit een bedrag uit het niets.
    PRICES = {};
    try {
      const el = root.querySelector('#khPrices, [data-kh-prices]');
      if (el && el.textContent.trim()) PRICES = JSON.parse(el.textContent) || {};
    } catch (e) { PRICES = {}; }

    loadMap();
    renderApps(); bind();
    const pre = new URLSearchParams(location.search).get('app');
    if (pre && APP_BY_KEY[pre]) { answers.app = pre; renderApps(); goTo(2); }
  }

  if (window.OlmiaEditor) OlmiaEditor.register({
    key: 'olmia-keuzehulp',
    match: (r) => !!(r && r.querySelector && r.querySelector('#kh')),
    init, teardown: () => {}
  });

  /* WACHTEN OP OlmiaUI (PRICE RULE, 2026-08-21).
     layout/theme.liquid laadt olmia-ui.js NA {{ content_for_layout }}, dus dit
     sectie-script draait er normaal vóór — bij een defer-script is readyState op
     dat moment al 'interactive', zodat de oude regel hier meteen init() deed en
     OlmiaUI.Money nog niet bestond. Alle defer-scripts zijn klaar op
     DOMContentLoaded; daarop wachten kost niets en is exact het patroon dat
     assets/olmia-cart-page.js al gebruikte. */
  if (window.OlmiaUI) init(document);
  else if (document.readyState === 'loading' || document.readyState === 'interactive') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else init(document);
})();
